import axios, { type AxiosError, type AxiosResponse } from 'axios'

import { trackDatadogError } from '@/shared/external/datadog'

import { UNKNOWN_VALUE } from '../constants'
import { buildFingerprint } from '../logging'
import { shouldSkipErrorReport } from './handledErrors'

import type { IRequestErrorInfo } from './handledErrors'

type TFingerprintedError = Error & { dd_fingerprint?: string }

/**
 * SSR에서 AxiosError가 unhandled로 빠져나가면 Next.js가 직렬화하다가
 * `Functions cannot be passed...`를 캐스케이드한다. 함수·클래스 인스턴스만 지우고
 * 호출자가 읽는 필드(message · status · data)는 남긴다.
 */
const sanitizeAxiosError = (error: AxiosError): void => {
  error.config = undefined
  error.request = undefined

  const response = error.response as
    | (Omit<AxiosResponse, 'config' | 'request'> & {
        config?: unknown
        request?: unknown
        headers: AxiosResponse['headers']
      })
    | undefined
  if (!response) return

  response.config = undefined
  response.request = undefined

  const headers = response.headers as unknown as
    | { toJSON?: () => Record<string, unknown> }
    | undefined
  if (headers && typeof headers.toJSON === 'function') {
    response.headers = headers.toJSON() as AxiosResponse['headers']
  }
}

const getRequestErrorInfo = (error: unknown): IRequestErrorInfo => {
  if (!axios.isAxiosError<{ code?: string }>(error)) {
    return { method: UNKNOWN_VALUE, url: UNKNOWN_VALUE }
  }

  return {
    method: error.config?.method?.toUpperCase() ?? UNKNOWN_VALUE,
    url: error.config?.url ?? UNKNOWN_VALUE,
    status: error.response?.status,
    code: error.response?.data?.code,
  }
}

export const handleRequestError = (error: unknown): void => {
  const info = getRequestErrorInfo(error)

  if (axios.isAxiosError(error)) {
    sanitizeAxiosError(error)
  }

  if (shouldSkipErrorReport(info)) return

  const { method, url, status, code } = info

  const reported: TFingerprintedError =
    error instanceof Error ? error : new Error(String(error))
  reported.dd_fingerprint = buildFingerprint({
    source: 'axios',
    method,
    url,
    status,
  })

  trackDatadogError(reported, {
    source: 'axios',
    method,
    url,
    status,
    code,
  })
}
